// Components
import { Box, Fade, Grid, Stack, Typography } from "@mui/material";

// Helpers & Utils
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusBadge } from "@smartrr/shared/customerPortal/components/AquariusBadge";
import { ConfirmCancelButtons } from "@smartrr/shared/customerPortal/components/ConfirmCancelButtons";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { countriesData } from "@smartrr/shared/data/countries";
import { ICancellationReason } from "@smartrr/shared/entities/CancellationRelationship";
import { IGift } from "@smartrr/shared/entities/Gift";
import { ISODateString } from "@smartrr/shared/entities/ISODateString";
import { IMailingAddressCreate, MailingAddressType } from "@smartrr/shared/entities/MailingAddress";
import { IPurchaseState, ISubscriptionProperties } from "@smartrr/shared/entities/PurchaseState";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { ISmartrrCountry } from "@smartrr/shared/interfaces/Country";
import { capitalizeFirstLetter } from "@smartrr/shared/utils/capitalizeFirstLetter";
import { formatAddressInline, formatGiftingAddressInline } from "@smartrr/shared/utils/formatAddressInline";
import {
  getNumberWithCountryCode,
  phoneCountryCode,
  validatePhoneNumber,
} from "@smartrr/shared/utils/phoneValidation";
import { localize, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  deleteGift,
  sendAsGift,
  updateSubscriptionAddress,
} from "@customer-app/state/actionCreators/customerPurchaseStates";
import { useCloseModal, useToggleModal } from "@customer-app/state/actionCreators/modal";
import {
  useSmartrrCustomerDispatch,
  useSmartrrCustomerSelector,
} from "@customer-app/state/typedCustomerReduxHooks";

import { AddressForm } from "../../../Settings/AddressForm";
import { useOrganization } from "@customer-app/account/views/OperationAquarius/utils";
interface IEditAddressViewProps {
  /** Toggle for Cancel button & setting view back to 'default' */
  setIconButtonInactive: () => void;

  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>;

  addressType?: MailingAddressType;

  gift?: IGift;

  saveUserCancellationData?: (cancel?: boolean) => void;
  cancellationReason?: ICancellationReason | undefined;
}

export const EditAddressView = ({
  setIconButtonInactive,
  delivery,
  addressType = MailingAddressType.FOR_CUSTOMER,
  gift,
  saveUserCancellationData,
  cancellationReason,
}: IEditAddressViewProps): JSX.Element => {
  const localizeShorthand = "subscriptions.yourSubscriptions.orderOptionsModal";
  const dispatch = useSmartrrCustomerDispatch();
  const closeModal = useCloseModal();
  const toggleModal = useToggleModal();
  const addressesState = useSmartrrCustomerSelector(state => state.addresses);
  const { shippingAddress } = delivery.purchaseState;
  const [countriesList, setCountriesList] = useState<ISmartrrCountry[]>();
  const [isLoading, setIsLoading] = useState<boolean>(addressesState.isLoading);
  const [addressFormValues, setAddressFormValues] = useState<IMailingAddressCreate | undefined>(undefined);
  const [isValid, setIsValid] = useState<boolean>(true);
  const organization = useOrganization();

  useEffect(() => {
    const fetchCountries = async () => {
      const countries = countriesData;
      setCountriesList(countries);
    };
    if (!countriesList) {
      fetchCountries();
    }
  }, []);

  const onConfirm = async (values: IMailingAddressCreate | undefined) => {
    setIsLoading(true);
    const address: IMailingAddressCreate = {
      ...values,
      addressType,
      shopifyUpdateDate: ISODateString.toString(new Date()),
    };
    if (gift) {
      const { note, notifyBy, cpsId, phone } = gift;
      //if the previous gift has the same schedule index as the current one
      if (gift.skdIdx === delivery.indexFromScheduleStart) {
        dispatch(deleteGift(gift));
      }
      dispatch(
        sendAsGift(
          {
            to: {
              address1: values?.address1,
              address2: values?.address2,
              city: values?.city,
              country: values?.country,
              countryCode: values?.countryCode,
              firstName: values?.firstName,
              lastName: values?.lastName,
              phone: values?.phone,
              province: values?.province,
              provinceCode: values?.provinceCode,
              zip: values?.zip,
            },
            stId: delivery.purchaseState.id,
            skdIdx: delivery.indexFromScheduleStart,
            note,
            notifyBy,
            cpsId,
            phone: values?.phone ?? phone,
          },
          localize(`${localizeShorthand}.editAddressView.giftAddressToast`),
          toast
        )
      );
      setIsLoading(false);
      closeModal();
      return;
    }
    if (address?.phone && address?.countryCode) {
      const isPhoneNumberValid = validatePhoneNumber(
        address?.phone.replaceAll("+" || "-", "")!,
        (address?.phoneCountryCode || address?.countryCode) as phoneCountryCode
      );

      if (!isPhoneNumberValid) {
        toast.error(typedLocalize("errorMessages.phoneNumber"));
        setIsLoading(false);
        return;
      }
    }
    const updatedPhoneNumber = getNumberWithCountryCode(address.phone!, address.countryCode as phoneCountryCode);

    const updatedAddress = {
      ...address,
      phone: updatedPhoneNumber || "",
    };

    const purchaseStateSubProperties: ISubscriptionProperties = {
      ...delivery.purchaseState.subProperties,
      phoneCountryCode: updatedAddress.phoneCountryCode,
    };

    const updateAddressRes = await dispatch(
      updateSubscriptionAddress(
        delivery.purchaseState.id,
        updatedAddress,
        toast,
        purchaseStateSubProperties,
        organization.orgName
      )
    );

    if (updateAddressRes.type === "PURCHASE_STATE_UPDATE_ADDRESS_SUCCESS") {
      toggleModal("CLOSE_MODAL", {}, true);
    }

    setIsLoading(false);
  };

  return (
    <Box>
      {isLoading ? (
        <Stack justifyContent="center" alignItems="center">
          <DotSpinner />
        </Stack>
      ) : (
        <React.Fragment>
          <Grid item xs={6} md={9} sx={{ marginTop: "15px" }}>
            <Stack direction="row" spacing="7px" sx={{ marginBottom: "3px" }}>
              <AquariusBadge label={typedLocalize("badgeStatus.current")} icon="pin" />
              {gift ? <AquariusBadge label="Gifted" icon="gift" /> : null}
            </Stack>
            {gift?.to?.address1 ? (
              <Box display="flex">
                <Typography variant="body1" className={prefixClassName("current-address-1")}>
                  {capitalizeFirstLetter(gift.to.address1)}
                </Typography>
              </Box>
            ) : shippingAddress?.address1 ? (
              <Box display="flex">
                <Typography variant="body1" className={prefixClassName("current-address-1")}>
                  {capitalizeFirstLetter(shippingAddress.address1)}
                </Typography>
              </Box>
            ) : null}
            <Box display="flex">
              <Typography variant="caption1" className={prefixClassName("current-address-2")}>
                {capitalizeFirstLetter(
                  (gift
                    ? formatGiftingAddressInline({
                        ...gift.to,
                        firstName: undefined,
                        lastName: undefined,
                        address1: undefined,
                      })
                    : formatAddressInline({ ...shippingAddress, address1: undefined })) ?? ""
                )}
              </Typography>
            </Box>
          </Grid>
          <Fade in={countriesList === undefined ? undefined : countriesList?.length! > 0}>
            <div>
              {countriesList?.length! > 0 ? (
                <AddressForm
                  countriesList={countriesList!}
                  addressType={gift ? MailingAddressType.GIFTING : MailingAddressType.FOR_CUSTOMER}
                  isEditing={false}
                  onChange={(values, isAddressValid) => {
                    setAddressFormValues(values);
                    setIsValid(isAddressValid);
                  }}
                />
              ) : null}
            </div>
          </Fade>
          <ConfirmCancelButtons
            disabled={!isValid}
            onCancel={setIconButtonInactive}
            onConfirm={async () => {
              await onConfirm(addressFormValues);
              if (cancellationReason && saveUserCancellationData) {
                saveUserCancellationData();
              }
            }}
          />
        </React.Fragment>
      )}
    </Box>
  );
};
